// src/components/pharmacies/PharmacyResultCard.jsx
import React from "react";
import FacilityPlaceholderThumb from "@/components/facilities/FacilityPlaceholderThumb.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  isFavoriteFacility,
  setFavoriteFacility,
  subscribeFacilityFavorites,
} from "@/services/facilityDirectoryPreferences.js";

const ICON_DISTANCE = "/icons/patient/distance_filled.svg";
const ICON_STAR = "/icons/patient/star_filled.svg";
const ICON_FAVORITE_FILLED = "/icons/patient/favorite.svg";
const ICON_FAVORITE_OUTLINE = "/icons/patient/favorite_outline.svg";

export default function PharmacyResultCard({ pharmacy, onSelect, onLocate }) {
  const { t } = useLanguage();
  const [isFavorite, setIsFavorite] = React.useState(() =>
    isFavoriteFacility("pharmacy", pharmacy?.id) || !!pharmacy?.isFavorite
  );

  if (!pharmacy) return null;

  const clickable = typeof onSelect === "function";
  const hasMapTarget =
    (Number.isFinite(Number(pharmacy?.lat)) && Number.isFinite(Number(pharmacy?.lng))) ||
    Boolean(String(pharmacy?.address || "").trim() || String(pharmacy?.name || "").trim());
  const canLocate = hasMapTarget && typeof onLocate === "function";

  React.useEffect(() => {
    const sync = () =>
      setIsFavorite(isFavoriteFacility("pharmacy", pharmacy?.id) || !!pharmacy?.isFavorite);
    sync();
    return subscribeFacilityFavorites(sync);
  }, [pharmacy?.id, pharmacy?.isFavorite]);

  const handleToggleFavorite = React.useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      const next = setFavoriteFacility("pharmacy", pharmacy?.id, !isFavorite);
      setIsFavorite(next);
    },
    [isFavorite, pharmacy?.id]
  );

  const handleOpenMap = React.useCallback(
    (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      onLocate?.(pharmacy);
    },
    [onLocate, pharmacy]
  );

  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onSelect(pharmacy) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onSelect(pharmacy);
            }
          : undefined
      }
      className={[
        "afrimed-rail afrimed-rail--comfortable rounded-[12px]",
        "bg-[rgb(var(--clr-surface-cont-highest))]",
        "shadow-[0_6px_16px_rgba(15,23,42,0.06)]",
        "border border-[rgba(15,23,42,0.06)]",
        "px-3 py-2",
        clickable ? "cursor-pointer" : "",
      ].join(" ")}
    >
      <div className="flex items-start px-2 pt-3 pb-2 gap-3">
        <FacilityPlaceholderThumb kind="pharmacy" label={pharmacy.name} />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-title-medium text-on-surface truncate">
                {pharmacy.name}
              </div>
              <div className="text-body-medium text-on-surface-var truncate">
                {pharmacy.address}
              </div>
            </div>

            <button
              type="button"
              aria-label={
                isFavorite
                  ? t(
                      "appointments.directory.pharmacies.favoriteRemoveAria",
                      "Remove pharmacy from favourites"
                    )
                  : t(
                      "appointments.directory.pharmacies.favoriteAddAria",
                      "Add pharmacy to favourites"
                    )
              }
              className="h-9 w-9 flex items-center justify-center"
              onClick={handleToggleFavorite}
            >
              <img
                src={isFavorite ? ICON_FAVORITE_FILLED : ICON_FAVORITE_OUTLINE}
                alt=""
                className="h-5 w-5"
                draggable="false"
              />
            </button>
          </div>

          <div className="mt-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-4 text-body-medium text-on-surface-var">
              <div className="flex items-center gap-1.5">
                <span>{pharmacy.distance || "—"}</span>
                <img
                  src={ICON_DISTANCE}
                  alt=""
                  className="h-[18px] w-[18px]"
                  draggable="false"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span>{pharmacy.rating || "—"}</span>
                <img
                  src={ICON_STAR}
                  alt=""
                  className="h-6 w-6 translate-y-[2px]"
                  draggable="false"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleOpenMap}
              aria-label={t("appointments.directory.common.mapAria", "Open location in maps")}
              disabled={!canLocate}
              className={`shrink-0 flex items-center gap-0 text-body-large ${
                canLocate ? "text-on-surface-var" : "text-on-surface-var opacity-60"
              }`}
            >
              <span>{t("appointments.directory.common.map", "Map")}</span>
              <img src={ICON_DISTANCE} alt="" className="h-6 w-6" draggable="false" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
